import { useState } from 'react'
import SetOfPricing, { SetOfPricingComponent } from '@/components/section/set-of-pricing'
import { slugify } from '@/utils/strings'
import '@/components/ui/pricing-card'
import { cx } from '@/utils/strings'
import type { Entry } from 'contentful'
import type { TypeSectionTabSectionSkeleton } from '@/types/ctf'
import { usePage } from '@inertiajs/react'
import { getLocale, localizedPath } from '@/utils/locale'
import RichText from '../ui/rich-text'
import GradientBackgroundWrapper from '@/components/ui/gradient-background-wrapper'

type TabSectionEntry = Entry<TypeSectionTabSectionSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type TabSectionProps = TabSectionEntry['fields'] & {
  contentfulId: string
  allExperiences?: any
  allAudiences?: any
  isHero?: boolean
  canExperiment?: boolean
  backgroundStyle?: string
  backgroundImage?: any
  __storybook_mockPathname?: string // Add this prop for Storybook
}

export default function TabSection({
  contentfulId,
  isHero,
  activeTab = 1,
  headline,
  subtext,
  eyebrowText,
  internalName,
  tabs,
  canExperiment = false,
  backgroundStyle,
  __storybook_mockPathname,
}: TabSectionProps) {
  const [activeTabIdx, setActiveTabIdx] = useState(activeTab ? activeTab - 1 : 0)

  // Use the mock pathname in Storybook context, otherwise use usePage
  let pathname = __storybook_mockPathname
  if (pathname === undefined) {
    const page = usePage()
    pathname = page.url
  }

  const handleSetActiveTab = (idx: number) => {
    if (idx === activeTabIdx) {
      return
    }

    const personalPricingPath = localizedPath('/pricing', getLocale(pathname))
    const businessPricingPath = localizedPath('/pricing/business', getLocale(pathname))
    if (pathname === personalPricingPath) {
      window.location.replace(businessPricingPath)
    } else if (pathname === businessPricingPath) {
      window.location.replace(personalPricingPath)
    } else if (pathname !== personalPricingPath || pathname !== businessPricingPath) {
      setActiveTabIdx(idx)
    }
  }

  const Headline = isHero ? 'h1' : 'h3'

  return (
    <section className={cx('max-w-8xl mx-auto', isHero && 'tabs-hero')} id={slugify(internalName)}>
      <GradientBackgroundWrapper backgroundStyle={backgroundStyle} isHero={isHero} innerClassName="pt-16">
        <div className="relative z-2">
          <div className="text-center clear-both px-5 lg:px-9">
            {eyebrowText && <span className="text-eyebrow uppercase">{eyebrowText}</span>}
            {headline && (
              <Headline
                className={cx(Headline === 'h1' ? 'h1' : 'h3', 'text-center m-auto text-balance header-gradient')}
              >
                {headline}
              </Headline>
            )}
            {subtext && (
              <div className="max-w-3xl mx-auto">
                <RichText body={subtext} />
              </div>
            )}
            <div
              className={cx(
                isHero ? 'my-5 lg:my-10' : 'my-5',
                ' w-auto inline-block rounded-full transition-all border-2 border-primaryBlue'
              )}
              role="tablist"
            >
              {tabs?.map((tab, idx) =>
                tab ? (
                  <button
                    key={`tab-${contentfulId}-${idx}`}
                    data-testid={`tab-${contentfulId}-${idx}`}
                    id={`tab-${contentfulId}-${idx}`}
                    onClick={() => handleSetActiveTab(idx)}
                    tabIndex={idx === 0 ? 0 : -1}
                    type="button"
                    role="tab"
                    aria-selected={idx === activeTabIdx}
                    aria-controls={`tabpanel-${contentfulId}-${idx}`}
                    className={cx(
                      'py-2 lg:py-3 px-8 lg:px-16 transition-all text-[15px] font-medium rounded-full',
                      idx === activeTabIdx
                        ? 'text-white bg-primaryBlue dark:text-secondary-900 dark:bg-white'
                        : 'hover:bg-bw-teal-light-highlight hover:dark:bg-secondary-700 text-primaryBlue dark:text-white bg-transparent',
                      idx === 0 && '-ml-[1px]',
                      idx === tabs.length - 1 && '-mr-[1px]'
                      //add a border on the right but not for the last tab
                      // idx !== tabs.length - 1 && 'border-r-1 border-primaryBlue'
                    )}
                  >
                    {tab.fields.headline || 'Missing Title!'}
                  </button>
                ) : null
              )}
            </div>
          </div>
          {tabs?.map((tab, idx) => {
            if (!tab) return null
            const { fields, sys } = tab
            return (
              <div
                role="tabpanel"
                aria-labelledby={`tab-${contentfulId}-${idx}`}
                key={`tabpanel-${contentfulId}-${idx}`}
                id={`tabpanel-${contentfulId}-${idx}`}
                data-testid={`tabpanel-${contentfulId}-${idx}`}
                className={cx('py-4', idx === activeTabIdx ? `block` : `hidden`)}
              >
                <div>
                  {canExperiment ? (
                    <SetOfPricing id={sys.id} {...fields} hideHeadline insideTabs />
                  ) : (
                    <SetOfPricingComponent {...fields} internalTitle="" hideHeadline insideTabs />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </GradientBackgroundWrapper>
    </section>
  )
}
